// apps/admin-ui/src/features/coin-transactions/filters.tsx
import { useNavigate } from '@tanstack/react-router';
import type { CoinTxReason, CoinTxStatus } from '@upme/contracts';
import { Badge } from '@upme/ui/components/badge';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { useEffect, useState } from 'react';
import { REASON_LABEL } from './reason-badge';
import { STATUS_LABEL } from './status-badge';

export interface CoinTransactionsFiltersState {
  status?: CoinTxStatus[] | undefined;
  reason?: CoinTxReason[] | undefined;
  userId?: string | undefined;
}

interface Props {
  value: CoinTransactionsFiltersState;
}

const STATUSES: CoinTxStatus[] = ['PENDING', 'POSTED', 'REVERTED'];
const REASONS: CoinTxReason[] = [
  'REFERRAL_BONUS',
  'MANUAL_ADJUST',
  'ADVERT_POST',
  'ORGANIZER_INVITE_BONUS',
  'ORGANIZER_WELCOME_BONUS',
  'WELCOME_BONUS',
  'RESERVATION_HOLD',
  'RESERVATION_PAYOUT',
  'RESERVATION_RELEASE',
];

// Простой uuid-чек — backend всё равно валидирует и возвращает 400 при
// невалидном; здесь только защищаемся от лишних запросов.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function CoinTransactionsFilters({ value }: Props) {
  const navigate = useNavigate();
  // Локальное состояние нужно только для userId (text input с дебаунсом «по
  // потере фокуса»), остальное apply'ится мгновенно при клике по chip'у.
  const [userIdLocal, setUserIdLocal] = useState(value.userId ?? '');

  useEffect(() => {
    setUserIdLocal(value.userId ?? '');
  }, [value.userId]);

  // Cursor сбрасываем при любом изменении фильтров — иначе клик по «другому»
  // фильтру оставит указатель в середину старого набора.
  const applySearch = (next: CoinTransactionsFiltersState) => {
    navigate({
      to: '/coin-transactions',
      search: () => {
        const out: Record<string, string | string[]> = {};
        if (next.status?.length) out.status = next.status;
        if (next.reason?.length) out.reason = next.reason;
        if (next.userId) out.userId = next.userId;
        return out;
      },
    });
  };

  const toggleStatus = (s: CoinTxStatus) => {
    const cur = new Set(value.status ?? []);
    if (cur.has(s)) cur.delete(s);
    else cur.add(s);
    applySearch({ ...value, status: cur.size ? Array.from(cur) : undefined });
  };

  const toggleReason = (r: CoinTxReason) => {
    const cur = new Set(value.reason ?? []);
    if (cur.has(r)) cur.delete(r);
    else cur.add(r);
    applySearch({ ...value, reason: cur.size ? Array.from(cur) : undefined });
  };

  const commitUserId = () => {
    const trimmed = userIdLocal.trim();
    if (trimmed === (value.userId ?? '')) return;
    if (trimmed === '') {
      applySearch({ ...value, userId: undefined });
      return;
    }
    if (!UUID_RE.test(trimmed)) return; // ignore некорректный ввод
    applySearch({ ...value, userId: trimmed });
  };

  const reset = () => {
    setUserIdLocal('');
    navigate({ to: '/coin-transactions', search: () => ({}) });
  };

  const hasAny = (value.status?.length ?? 0) > 0 || (value.reason?.length ?? 0) > 0 || !!value.userId;

  return (
    <div className="border rounded-md p-4 mb-4 space-y-4">
      <div className="space-y-2">
        <Label className="text-xs">Статус</Label>
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => {
            const active = value.status?.includes(s) ?? false;
            return (
              <button key={s} type="button" onClick={() => toggleStatus(s)} className="cursor-pointer">
                <Badge variant={active ? 'default' : 'outline'}>{STATUS_LABEL[s]}</Badge>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Причина</Label>
        <div className="flex flex-wrap gap-2">
          {REASONS.map((r) => {
            const active = value.reason?.includes(r) ?? false;
            return (
              <button key={r} type="button" onClick={() => toggleReason(r)} className="cursor-pointer">
                <Badge variant={active ? 'default' : 'outline'}>{REASON_LABEL[r]}</Badge>
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-md">
        <Label htmlFor="userId" className="text-xs">
          Пользователь (UUID)
        </Label>
        <Input
          id="userId"
          type="text"
          placeholder="00000000-0000-0000-0000-000000000000"
          value={userIdLocal}
          onChange={(e) => setUserIdLocal(e.target.value)}
          onBlur={commitUserId}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitUserId();
            }
          }}
          className="font-mono text-xs"
        />
      </div>

      {hasAny && (
        <div>
          <Button variant="ghost" size="sm" onClick={reset}>
            Сбросить фильтры
          </Button>
        </div>
      )}
    </div>
  );
}
